import { useContext, useEffect, useMemo, useState } from 'react';

import { Button as DesignSystemButton, Dialog } from '@capsule/consumer-design-system';

import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { AccountPageItem } from 'src/app/components/Elements/Account/AccountPageItem';
import Button from 'src/app/components/Elements/Button';
import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { Card, CardItem } from 'src/app/components/Elements/Card';
import Spinner from 'src/app/components/Elements/Spinner';
import { FamilyModal } from 'src/app/components/Modal/Family/container';
import { CardItemWithDeleteButton } from 'src/app/components/PageSections/CardItemOverflowMenuWrapper/CardDeleteButton';
import { useAccount, useAccountUpdate } from 'src/app/hooks/data/account';
import { useDeliveryAddressDelete, useDeliveryAddressListByAccountId } from 'src/app/hooks/data/deliveryAddress';
import { usePatientList } from 'src/app/hooks/data/patient';
import { usePaymentCardDelete, usePaymentCardListByAccountId } from 'src/app/hooks/data/paymentCard';
import { useUser } from 'src/app/hooks/data/user';
import { useAccountPageAddressData } from 'src/app/hooks/feature_data/useAccountPageAddressData';
import useAuth from 'src/app/hooks/useAuth';
import { InsuranceStateContext } from 'src/app/providers/InsuranceStateProvider';
import { AccountPageAddAddress, AccountPageCreditCard, AccountPageInsurance, medications } from 'src/app/router/paths';
import { segmentTrack } from 'src/app/state/actions';
import { getHasFetchedInsurance } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import type { PaymentCard } from 'src/app/types';
import { formatPostgresDate, returnCardImage } from 'src/app/utils';
import { smoothScroll } from 'src/app/utils/smoothScroll';
import { CAPSULE_ICON_STRINGS, POPUP_MENU_LABELS } from 'src/consts';
import { DELETE_INSURANCE_BUTTON } from 'src/consts/testIDs';

import type { PropsFromRedux } from './container';
import { FamilyMemberSection } from './FamilyMembers';
import styles from './styles.module.scss';

const BottomModalButton = styled(DesignSystemButton)`
  margin-top: ${props => props.theme.space.s4};
  text-decoration: underline;
`;

const Renderer = ({
  cards,
  addresses,
  dispatchOpenModal,
  isInsuranceUploadSuccessful,
  insurances,
  insuranceSubmittedOn,
  dispatchGetInsurance,
  dispatchDeleteInsuranceModalSegmentEvent,
  dispatchDeleteInsurance,
  dispatchDeleteInsuranceSegmentEvent,
  dispatchAddInsuranceSegmentEvent,
  dispatchCloseModal,
  //dispatchDeletePaymentCard,
  //dispatchAccountDeleteAddress,
  dispatchGotoAddAddress,
  dispatchTrackAddressChange,
  dispatchSegmentTrack,
}: PropsFromRedux) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const hasFetchedInsurance = useSelector(getHasFetchedInsurance);
  const [hasViewAccountEventFired, setHasViewAccountEventFired] = useState(false);

  useEffect(() => {
    if (hasFetchedInsurance && !hasViewAccountEventFired) {
      setHasViewAccountEventFired(true);
      dispatch(
        segmentTrack(UNIFIED.viewYourAccount, {
          existing_insurances: insurances ? insurances.length : 0,
          has_pending_insurance: !!insuranceSubmittedOn,
        })
      );
    }
  }, [dispatch, hasFetchedInsurance, hasViewAccountEventFired, insuranceSubmittedOn, insurances]);

  const { hash } = useLocation();

  const [isSubmitting, setSubmitting] = useState(false);
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';
  const { data: pharmakonAccount } = useAccount(parseInt(accountId), { enabled: Boolean(accountId) });

  const { mutateAsync: updateAccountAsync } = useAccountUpdate(parseInt(accountId));

  const { mutateAsync: deleteAddressAsync } = useDeliveryAddressDelete();

  const { mutateAsync: deletePaymentCardAsync } = usePaymentCardDelete(parseInt(accountId));

  // Get patients
  const { data: patients, isLoading: isPatientLoading } = usePatientList(parseInt(accountId), Boolean(accountId));

  // Get payment cards.
  const { data: paymentCards, isLoading: isPaymentCardsLoading } = usePaymentCardListByAccountId(parseInt(accountId), {
    enabled: Boolean(accountId),
  });

  // Get delivery addresses.
  const { data: deliveryAddresses, isLoading: isDeliveryAddressesLoading } = useDeliveryAddressListByAccountId(
    parseInt(accountId),
    { enabled: Boolean(accountId) }
  );

  const isLoading = !hasFetchedInsurance || isPaymentCardsLoading || isDeliveryAddressesLoading || isPatientLoading;

  const handleDeleteCard = async (paymentCardId: number) => {
    setSubmitting(true);
    dispatchCloseModal();
    await deletePaymentCardAsync(paymentCardId);
    await updateAccountAsync({ stripeDefaultCardId: null });

    dispatchSegmentTrack(UNIFIED.deleteCreditCard, { source: 'Your Account' });
    setSubmitting(false);
  };

  const { setAddressData } = useAccountPageAddressData();
  useEffect(() => {
    setAddressData({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAddress = async (addressId: string) => {
    setSubmitting(true);
    dispatchCloseModal();
    await deleteAddressAsync(parseInt(addressId));
    dispatchSegmentTrack(UNIFIED.deleteDeliveryAddress, { location: 'Your Account' });
    await setAddressData({});
    setSubmitting(false);
  };

  const handleDeleteInsurance = async (id: string, patient_id: string, insurance_company: string) => {
    setSubmitting(true);
    dispatchCloseModal();

    // Being abundantly careful to not expose the insurance api's. This will never be reached if the flag is
    // false but want to shore up all api calls under the flag.
    const insuranceCount = insurances ? insurances.length : 0;
    const attributes = {
      existing_insurances: insuranceCount,
      insurance_id: id,
      insurance_company,
    };

    await Promise.all([dispatchDeleteInsuranceSegmentEvent(attributes), dispatchDeleteInsurance(id, patient_id)]);
    await dispatchGetInsurance(accountId);

    setSubmitting(false);
  };

  const [showModalForDuplicateInsuranceSubmissions, setShowModalForDuplicateInsuranceSubmissions] = useState(false);

  const handleOnAddInsuranceClick = () => {
    const insuranceCount = insurances ? insurances.length : 0;
    const attributes = {
      existing_insurances: insuranceCount,
      has_pending_insurance: !!insuranceSubmittedOn,
    };
    dispatchAddInsuranceSegmentEvent(attributes);
    if (hasPendingInsurance) {
      setShowModalForDuplicateInsuranceSubmissions(true);
    } else {
      history.push(AccountPageInsurance.toPath());
    }
  };

  useEffect(() => {
    setTimeout(function () {
      if (hash === '#deliveryAddresses') {
        smoothScroll('deliveryAddresses');
      }
    }, 1000);
  }, [hash]);

  useEffect(() => {
    if (accountId) {
      dispatchGetInsurance(accountId);
    }
  }, [dispatchGetInsurance, accountId]);

  useEffect(() => {
    if (!isInsuranceUploadSuccessful && accountId) {
      dispatchGetInsurance(accountId);
    }
  }, [isInsuranceUploadSuccessful, dispatchGetInsurance, accountId]);

  const insuranceContext = useContext(InsuranceStateContext);

  const hasInsurance = useMemo(() => hasFetchedInsurance && !isEmpty(insurances), [insurances, hasFetchedInsurance]);
  const hasPendingInsurance = !!insuranceSubmittedOn;

  if (isSubmitting || isLoading) return <Spinner centered />;
  return (
    <div className={styles.component}>
      <FamilyModal />
      <FamilyMemberSection
        showFamilyPrompt={patients ? patients?.length == 1 : false}
        patients={patients ?? []}
        email={user?.email ?? ''}
        phone={pharmakonAccount?.phone ?? ''}
      />

      <h2 className={styles.sectionTitle}>INSURANCE</h2>
      <Card>
        {/* The insurance pending screen always will be at top if there is a pending state */}
        {hasPendingInsurance && (
          <CardItem>
            <div className={styles.pending}>
              <p className={styles.insuranceHeaderStyle}>Insurance card added. Pending review.</p>
              <p className={styles.insuranceSubHeaderStyle}>SUBMITTED: {formatPostgresDate(insuranceSubmittedOn)}</p>
            </div>
          </CardItem>
        )}
        {!hasInsurance && !insuranceSubmittedOn && (
          <CardItem>
            <div className={styles.emptyStateBoxInsurance}>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.insurance} />
              <p>You have no insurance saved to your account.</p>
            </div>
          </CardItem>
        )}
        {hasInsurance && (
          <CardItem>
            {map(insurances, (insurance, i) => {
              const {
                id,
                cardholderFirstName,
                cardholderLastName,
                cardholderId,
                patientId,
                patientPlanDisplayName,
                startDate,
              } = insurance || {};
              const insuranceCount = insurances ? insurances.length : 0;
              const handleDelete = () => {
                dispatchDeleteInsuranceModalSegmentEvent({
                  existing_insurances: insuranceCount,
                  insurance_id: id,
                  insurance_company: patientPlanDisplayName,
                });
                dispatchOpenModal({
                  modalType: 'confirm',
                  handleConfirmClick: () => handleDeleteInsurance(id, patientId, patientPlanDisplayName),
                  confirmMessage: 'Are you sure you want to delete this card?',
                });
              };

              return (
                <CardItemWithDeleteButton
                  data-testid={DELETE_INSURANCE_BUTTON}
                  handleDelete={handleDelete}
                  key={`key-${i}`}
                >
                  <div className={styles.insuranceInfo}>
                    {cardholderFirstName && cardholderLastName && (
                      <small className="micro consast inspectlet-sensitive">
                        {cardholderFirstName} {cardholderLastName}
                      </small>
                    )}
                    <p className={styles.insuranceHeaderStyle}>{patientPlanDisplayName}</p>
                    <p className={styles.insuranceHeaderStyle}>ID: {cardholderId}</p>
                    <p className={styles.insuranceSubHeaderStyle}>ADDED: {startDate}</p>
                  </div>
                </CardItemWithDeleteButton>
              );
            })}
          </CardItem>
        )}
      </Card>
      <Button data-cy="addInsuranceButton" onClick={handleOnAddInsuranceClick} style="secondary" size="medium" block>
        {hasInsurance || hasPendingInsurance ? 'ADD ANOTHER INSURANCE' : 'ADD INSURANCE'}
      </Button>

      <h2 id="deliveryAddresses" className={styles.sectionTitle}>
        DELIVERY ADDRESSES
      </h2>
      <Card>
        <CardItem>
          {isEmpty(deliveryAddresses) ? (
            <div className={styles.emptyStateBox}>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.checkout_address_empty} />
              <p>You have no saved addresses.</p>
            </div>
          ) : (
            deliveryAddresses?.map((deliveryAddress, i) => {
              const { address, addressTwo, state, city, zipCode, instructions, isDoorman } = deliveryAddress;

              const handleDelete = () =>
                dispatchOpenModal({
                  modalType: 'confirm',
                  handleConfirmClick: () => handleDeleteAddress(deliveryAddress.id ?? ''),
                  confirmMessage: 'Are you sure you want to delete this address?',
                });

              const handleEdit = () => {
                Promise.all([
                  setAddressData({ existingAddress: deliveryAddress }),
                  /*
                   * This is a hack to ensure we wait for the query to update
                   * before navigating to next page. We should be able to remove
                   * once we refactor the react-query hooks that deal with local
                   * and session storage.
                   */
                  new Promise(resolve => setTimeout(resolve, 0)),
                ]).then(() => {
                  dispatchTrackAddressChange(POPUP_MENU_LABELS.edit);
                  history.push(AccountPageAddAddress.toPath());
                });
              };

              const menuActions = {
                items: [
                  { label: POPUP_MENU_LABELS.edit, action: handleEdit },
                  { label: POPUP_MENU_LABELS.delete, action: handleDelete },
                ],
              };

              return (
                <AccountPageItem menuActions={menuActions} key={`key-${i}`}>
                  <div className={styles.deliveryStreet}>
                    <p>{address}</p>
                    {addressTwo && <p>{addressTwo}</p>}
                    <p>
                      {city}, {state} {zipCode}
                    </p>
                    {isDoorman && <p className={styles.doorman}>Leave with Doorman</p>}
                    <p className={styles.doorman}>{instructions}</p>
                  </div>
                </AccountPageItem>
              );
            })
          )}
        </CardItem>
      </Card>
      <Button onClick={dispatchGotoAddAddress} style="secondary" size="medium" block data-cy="add-delivery-address">
        {isEmpty(addresses) ? 'add an address' : 'add a new address'}
      </Button>
      <h2 className={styles.sectionTitle}>MANAGE PAYMENTS</h2>
      <Card>
        <CardItem>
          {isEmpty(paymentCards) ? (
            <div className={styles.emptyStateBox}>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.checkout_payment_empty} />
              <p className={styles.emptyStateText}>You have no saved payments.</p>
            </div>
          ) : (
            paymentCards?.map((card: PaymentCard) => {
              const handleDeleteClick = () =>
                dispatchOpenModal({
                  modalType: 'confirm',
                  handleConfirmClick: () => handleDeleteCard(card.id),
                  confirmMessage: 'Are you sure you want to delete this card?',
                });

              const menuActions = {
                items: [{ label: POPUP_MENU_LABELS.delete, action: handleDeleteClick }],
              };

              return (
                <AccountPageItem menuActions={menuActions} key={`key-${card.id}`}>
                  <div className={styles.cardInfo}>
                    <div className={styles.logo}>{returnCardImage(card.brand)}</div>
                    <div>
                      <p>{card.brand}</p>
                      <p>•••• {card.last4}</p>
                      <p className={styles.expires}>Expires: {card.expirationDate}</p>
                    </div>
                  </div>
                </AccountPageItem>
              );
            })
          )}
        </CardItem>
      </Card>
      <LinkButton to={AccountPageCreditCard.toPath()} style="secondary" size="medium" block data-cy="add-credit-card">
        {isEmpty(cards) ? 'add a payment' : 'add a new payment'}
      </LinkButton>
      {showModalForDuplicateInsuranceSubmissions && (
        <Dialog
          isOpen={true}
          onOpenChange={() => null}
          onOpen={() => null}
          onClose={() => {
            setShowModalForDuplicateInsuranceSubmissions(false);
          }}
          title="Are you sure you want to submit your insurance again?"
          description="We are currently reviewing your insurance information. We’ll send you a confirmation text in one hour once it’s been verified."
        >
          <>
            <DesignSystemButton
              onClick={() => {
                setShowModalForDuplicateInsuranceSubmissions(false);
              }}
              variant="contained-secondary-lg"
              isFullWidth
              data-cy="insurance-account-page-modal-no-take-me-back-button"
            >
              NO, TAKE ME BACK
            </DesignSystemButton>
            <BottomModalButton
              onClick={() => {
                history.push(AccountPageInsurance.toPath());
              }}
              variant="text-md"
              isFullWidth
              data-cy="insurance-account-page-modal-add-another-insurance-button"
            >
              ADD ANOTHER INSURANCE
            </BottomModalButton>
          </>
        </Dialog>
      )}

      {insuranceContext && insuranceContext.insuranceState?.isInsuranceSuccessModalActive && (
        <Dialog
          isOpen={true}
          onOpenChange={() => null}
          onOpen={() => null}
          onClose={() => {
            insuranceContext.saveInsuranceState({ isInsuranceSuccessModalActive: false });
          }}
          title="Great! We are reviewing your insurance information"
          description="We’ll send you a text shortly, once we’ve verified your insurance."
        >
          <>
            <DesignSystemButton
              onClick={() => {
                insuranceContext.saveInsuranceState({ isInsuranceSuccessModalActive: false });
                history.push(medications.toPath());
              }}
              variant="contained-secondary-lg"
              isFullWidth
              data-cy="insurance-account-page-modal-got-it-button"
            >
              GOT IT
            </DesignSystemButton>
          </>
        </Dialog>
      )}
    </div>
  );
};

export { Renderer };
